import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  ownerOnboardingCompletionResponseSchema,
  ownerOnboardingProfileResponseSchema,
} from "@/schemas/ownerOnboarding";
import type {OwnerOnboardingCompletionResponse, OwnerOnboardingProfileRequest, OwnerOnboardingProfileResponse, TermsAcceptanceRequest} from "@/schemas/ownerOnboarding";

import { apiRequest, apiRequestFormData } from "./apiUtils";

export const ownerOnboardingKeys = {
  me: ["owner-onboarding", "me"] as const,
};

export async function getOwnerOnboardingProfile(): Promise<OwnerOnboardingProfileResponse> {
  const res = await apiRequest<unknown>("owner-onboarding/me", { method: "GET" });
  return ownerOnboardingProfileResponseSchema.parse(res);
}

export async function saveOwnerOnboardingProfile(
  body: OwnerOnboardingProfileRequest,
): Promise<OwnerOnboardingProfileResponse> {
  const res = await apiRequest<unknown>("owner-onboarding/me", {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return ownerOnboardingProfileResponseSchema.parse(res);
}

export async function acceptOwnerTerms(
  body: TermsAcceptanceRequest,
): Promise<OwnerOnboardingProfileResponse> {
  const res = await apiRequest<unknown>("owner-onboarding/me/terms", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return ownerOnboardingProfileResponseSchema.parse(res);
}

export async function uploadOwnerRestaurantImage(
  file: File,
): Promise<string | undefined> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await apiRequestFormData<{ url?: string }>(
    "owner-onboarding/me/restaurant-image",
    fd,
  );
  return res.url;
}

export async function uploadOwnerPhoto(file: File): Promise<string | undefined> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await apiRequestFormData<{ url?: string }>(
    "owner-onboarding/me/photo",
    fd,
  );
  return res.url;
}

export async function completeOwnerOnboarding(): Promise<OwnerOnboardingCompletionResponse> {
  const res = await apiRequest<unknown>("owner-onboarding/me/complete", {
    method: "POST",
  });
  return ownerOnboardingCompletionResponseSchema.parse(res);
}

export function useOwnerOnboardingDraft() {
  return useQuery<OwnerOnboardingProfileResponse>({
    queryKey: ownerOnboardingKeys.me,
    queryFn: getOwnerOnboardingProfile,
  });
}

export function useSaveOwnerOnboarding() {
  const qc = useQueryClient();
  return useMutation<
    OwnerOnboardingProfileResponse,
    Error,
    OwnerOnboardingProfileRequest
  >({
    mutationFn: saveOwnerOnboardingProfile,
    onSuccess: (data) => qc.setQueryData(ownerOnboardingKeys.me, data),
  });
}
